import React, { useState, useEffect, useRef } from "react";
import posthog from "posthog-js";
import { calculateSimulator } from "../utils/SimCalculator.js";
import SimulatorForm from "component/SimulatorForm.js";
import SimulatorResults from "component/SimulatorResults.js";
import SimulatorBilan from "component/SimulatorBilan.js";

const EVENT_PREFIX = "luniapp_simulator_";
const isPosthogDebugEnabled = process.env.REACT_APP_POSTHOG_DEBUG === "true" || process.env.REACT_APP_ENV === "staging";

const formatHotelBrandName = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .split(/[_\s]+/)
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ""))
    .join(" ");
};

const Simulator = () => {
  const [step, setStep] = useState("form");
  const prevStepRef = useRef(step);
  const [formData, setFormData] = useState({
    numRooms: "",
    hotelType: "",
    numStars: 3,
    segment: "",
    heatingType: "electrique",
    occupationRate: "",
    energyPrice: 0.17,
    waterPrice: 4, // €/m³ (affiché en m³ ; converti en €/L pour le calcul)
    offerType: "achat",
  });
  const [results, setResults] = useState(null);

  const trackEvent = (eventName, properties = {}) => {
    const fullEventName = `${EVENT_PREFIX}${eventName}`;
    const payload = {
      ...properties,
      source_app: "luniapp",
      app_scope: "simulator_accor",
    };
    posthog.capture(fullEventName, payload);
    if (isPosthogDebugEnabled) {
      console.info("[posthog] capture", fullEventName, payload);
    }
  };

  useEffect(() => {
    trackEvent("setup_page_viewed");
  }, []);

  useEffect(() => {
    if (step === "results" && results && prevStepRef.current !== "results") {
      trackEvent("results_page_viewed", {
        marque_accor: formatHotelBrandName(results.selectedHotel),
        marque_accor_key: results.selectedHotel,
        hotel_reference: results.hotelType,
        nombre_chambres: results.nbRooms,
        taux_occupation: results.occupationRate,
        prix_energie: results.energyPrice,
        prix_eau: results.waterPrice,
      });
    }
    prevStepRef.current = step;
  }, [step, results]);

  const handleCalculate = () => {
    if (
        !formData.numRooms ||
        !formData.hotelType ||
        !formData.occupationRate ||
        !formData.energyPrice ||
        !formData.waterPrice
      ) {
        console.warn("Formulaire incomplet !");
        return;
      }

    const baseResults = calculateSimulator({
      nbRooms: Number(formData.numRooms),
      hotelType: formData.hotelType,
      occupationRate: Number(formData.occupationRate),
      heatingType: formData.heatingType,
      energyPrice: Number(formData.energyPrice),
      waterPrice: Number(formData.waterPrice) / 1000, // formulaire en €/m³ → calcul en €/L
    });

    setResults({
      ...baseResults,
      selectedHotel: formData.hotelType,
      occupationRate: Number(formData.occupationRate),
      energyPrice: Number(formData.energyPrice),
      waterPrice: Number(formData.waterPrice), // stocké en €/m³ pour l’affichage
      nbRooms: Number(formData.numRooms),
    });
    setStep("results");
  };

  const handleUpdateParams = (updatedParams) => {
    const nextFormData = {
      ...formData,
      ...updatedParams,
    };

    setFormData(nextFormData);

    const baseResults = calculateSimulator({
      nbRooms: Number(nextFormData.numRooms),
      hotelType: nextFormData.hotelType,
      occupationRate: Number(nextFormData.occupationRate),
      heatingType: nextFormData.heatingType,
      energyPrice: Number(nextFormData.energyPrice),
      waterPrice: Number(nextFormData.waterPrice) / 1000, // formulaire en €/m³ → calcul en €/L
    });

    setResults({
      ...baseResults,
      selectedHotel: nextFormData.hotelType,
      occupationRate: Number(nextFormData.occupationRate),
      energyPrice: Number(nextFormData.energyPrice),
      waterPrice: Number(nextFormData.waterPrice), // stocké en €/m³ pour l’affichage
      nbRooms: Number(nextFormData.numRooms),
    });
  };

  const goBackToForm = () => setStep("form");

  return (

      <div className="simulator_P1_container simulator-form">
              {step === "form" && (
                <div className="simulator-form-container">
                  <SimulatorForm
                      formData={formData}
                      setFormData={setFormData}
                      onSubmit={handleCalculate} />
                </div>
              )}

            {step === "results" && results && (
              <div className="simulator-results-wrapper">
                <SimulatorResults
                  results={results}
                  onEdit={goBackToForm}
                  onDevisClick={() => setStep("bilan")}
                  // @ts-ignore - prop supportée par SimulatorResults (JS) mais pas déclarée côté types implicites
                  onParamsChange={handleUpdateParams}
                />
              </div>
            )}

            {step === "bilan" && (
              <SimulatorBilan onEdit={goBackToForm} />
            )}
    </div>
  );  
};

export default Simulator;
